/* =============================================================================
   Crumpaw · shared sticky add-to-cart bar
   -----------------------------------------------------------------------------
   <cp-sb-sticky-atc> — one component for every product template.

   The templates do not agree on anything: the harness has a hand-rolled buy box,
   the bowls each have their own hero section, the default template is stock
   Horizon. So this reads the page rather than being told about it.

     · The real add-to-cart button is found by selector chain, and its own
       computed style is copied onto this button — fill, font, weight, tracking,
       radius and height. Nothing about any template's colours lives here.
     · The personalisation fields are whatever `properties[...]` controls that
       template's form actually has. A field holds the button in state 1 only if
       it is enabled, visible and marked required — so the harness's optional
       phone, and every radio group with a default, are ignored.
     · Adding to cart clicks the real button rather than posting a form of its
       own, so every line item property, the theme's AJAX cart and any per-
       template click handler behave exactly as they do without this bar.
   ========================================================================== */

(() => {
  'use strict';

  const CART_UPDATE = 'cart:update';
  const HIDE_ABOVE = 768;

  /* Style properties lifted from the template's own add-to-cart button. */
  const COPIED = [
    'backgroundColor',
    'backgroundImage',
    'color',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'fontStyle',
    'lineHeight',
    'letterSpacing',
    'textTransform',
    'borderRadius',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderStyle',
    'borderColor',
    'boxShadow',
  ];

  const DEFAULT_BUTTONS = [
    '[data-cph="cta"]',
    '.cpsb-cta',
    'form[data-type="add-to-cart-form"] button[name="add"]',
    'form[action*="/cart/add"] button[name="add"]',
  ];

  const readConfig = (host) => {
    const node = host.querySelector('[data-cp-sb-sticky-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (error) {
      return {};
    }
  };

  const interpolate = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      values[key] == null ? '' : String(values[key])
    );

  const setText = (node, value) => {
    if (node && node.textContent !== value) node.textContent = value;
  };

  /**
   * Learn a shop's money shape from a price Liquid has already rendered, so the
   * total can be picked out of the real button's label whatever the currency,
   * symbol position and separators happen to be.
   *
   * @param {string} sample A formatted price, e.g. "$59.00" or "59,00 EUR".
   * @returns {RegExp|null} Null when the sample carries nothing to anchor on.
   */
  const moneyPattern = (sample) => {
    const match = String(sample || '').trim().match(/^(\D*?)\d[\d.,\s]*(\D*)$/);
    if (!match) return null;
    const esc = (value) => value.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pre = esc(match[1]);
    const post = esc(match[2]);
    if (!pre && !post) return null;
    return new RegExp(pre + '\\s*\\d[\\d.,]*' + (post ? '\\s*' + post : ''));
  };

  /** Shopify's money_format, for the templates whose button prints no price. */
  const formatMoney = (cents, format) => {
    const amount = Number(cents) || 0;
    const withDelimiters = (precision, thousands, decimal) => {
      const parts = (amount / 100).toFixed(precision).split('.');
      const grouped = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${grouped}${decimal}${parts[1]}` : grouped;
    };
    if (typeof format !== 'string' || !format.includes('{{')) {
      return `$${withDelimiters(2, ',', '.')}`;
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, token) => {
      switch (token) {
        case 'amount_no_decimals': return withDelimiters(0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(2, "'", '.');
        case 'amount_with_space_separator': return withDelimiters(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(0, ' ', '.');
        case 'amount_with_period_and_space_separator': return withDelimiters(2, ' ', '.');
        default: return withDelimiters(2, ',', '.');
      }
    });
  };

  /** Does this control hold something the shopper actually put there? */
  const isFilled = (el) => {
    if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
    if (el.type === 'file') return !!(el.files && el.files.length);
    return String(el.value == null ? '' : el.value).trim() !== '';
  };

  /* A control the shopper can see and use. `hidden` inputs carry values the page
     derived for itself — a crop box, a row count — and are never their job. */
  const isLive = (el) => !el.disabled && el.type !== 'hidden';

  /* `required` is what the bowls and the harness put on the name field;
     `aria-required` is what the photo uploads use, because a file input marked
     required blocks a form the shopper has legitimately filled another way. */
  const isRequired = (el) =>
    el.required === true || el.getAttribute('aria-required') === 'true';

  /* Values worth printing in the selection line: what the shopper typed, not
     what they picked from a swatch. Radio groups all carry a default, so listing
     them would fill the line with words nobody chose. */
  const isTypedValue = (el) =>
    el.type === 'text' || el.type === 'tel' || el.type === 'email' ||
    el.type === 'search' || el.type === 'number' || el.tagName === 'TEXTAREA';

  class CpSbStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.config = readConfig(this);
      this.variants = this.config.variants || {};
      this.inCart = this.config.inCart === true;
      this.state = 'name';

      this.refs = {
        line: this.querySelector('[data-cp-sb-sticky="line"]'),
        cta: this.querySelector('[data-cp-sb-sticky="cta"]'),
        label: this.querySelector('[data-cp-sb-sticky="label"]'),
        bar: this.querySelector('[data-cp-sb-sticky="bar"]'),
      };
      if (!this.refs.bar || !this.refs.cta) return;

      this.mainButton = this.findMainButton();
      this.form = this.mainButton
        ? this.mainButton.form || this.mainButton.closest('form')
        : document.querySelector('form[data-type="add-to-cart-form"]');

      // With no add-to-cart button on the page there is nothing to mirror and
      // nothing to scroll past, so stay out of the way entirely.
      if (!this.mainButton || !this.form) {
        this.refs.bar.remove();
        return;
      }

      this.bind();
      this.measure();
      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onDocChange) {
        document.removeEventListener('change', this.onDocChange, true);
        document.removeEventListener('input', this.onDocChange, true);
        document.removeEventListener('click', this.onDocChange, true);
      }
      if (this.onCart) document.removeEventListener(CART_UPDATE, this.onCart);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.trailing) window.clearTimeout(this.trailing);
    }

    /* ------------------------------------------------------------- finding */

    findMainButton() {
      const selectors = this.config.buttonSelectors || DEFAULT_BUTTONS;
      for (let i = 0; i < selectors.length; i += 1) {
        const found = document.querySelector(selectors[i]);
        if (found) return found;
      }
      return null;
    }

    /* ------------------------------------------------------------- wiring */

    bind() {
      this.refs.cta.addEventListener('click', (event) => this.onClick(event));

      // Anything the shopper touches can change the variant, a personalisation
      // field, or both. The trailing pass catches pickers that write the variant
      // id asynchronously after their own handler has run.
      this.onDocChange = () => this.schedule();
      document.addEventListener('change', this.onDocChange, true);
      document.addEventListener('input', this.onDocChange, true);
      // Buttons change a selection without firing either of the above — the
      // harness's patch presets are buttons, not inputs. The render is
      // frame-debounced, so listening this broadly costs nothing.
      document.addEventListener('click', this.onDocChange, true);

      this.onCart = () => this.refreshCart();
      document.addEventListener(CART_UPDATE, this.onCart);

      this.onResize = () => {
        this.measure();
        this.schedule();
      };
      window.addEventListener('resize', this.onResize);

      // Show the bar once the real button is behind the shopper, not merely
      // off screen — before they have scrolled to it at all, it is just noise.
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          const top = entry.rootBounds ? entry.rootBounds.top : 0;
          const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < top;
          // Crossings are rare, and the real button may not have had a size at
          // all when this first ran — inside a step that was still closed, say.
          this.measure();
          this.setVisible(passed);
        },
        { threshold: 0 }
      );
      this.observer.observe(this.mainButton);

      // A webfont landing after first paint changes the real button's height.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.measure()).catch(() => {});
      }
    }

    schedule() {
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.render());
      if (this.trailing) window.clearTimeout(this.trailing);
      this.trailing = window.setTimeout(() => this.render(), 200);
    }

    setVisible(visible) {
      this.refs.bar.classList.toggle('is-visible', !!visible);
    }

    /* -------------------------------------------------------- measurement */

    /**
     * Copy the template's own add-to-cart button onto this one, and line this
     * one up with the column that one sits in. Re-run on resize, because a
     * template can restyle its button at a breakpoint.
     */
    measure() {
      const { cta } = this.refs;
      const style = window.getComputedStyle(this.mainButton);
      COPIED.forEach((prop) => {
        cta.style[prop] = style[prop];
      });

      const rect = this.mainButton.getBoundingClientRect();
      if (rect.height > 0) cta.style.height = `${rect.height}px`;

      const gutter = this.gutter(rect);
      if (gutter != null) {
        this.refs.bar.style.setProperty('--cp-sb-sticky-gutter', `${Math.round(gutter)}px`);
      }

      this.reserve();
    }

    /**
     * The page's own horizontal padding. On the Crumpaw heroes the add-to-cart
     * button fills the content column, so its left edge is the gutter exactly.
     * On stock Horizon it sits beside a quantity picker and is nowhere near
     * full width — taking its left edge there would squeeze this button to a
     * third of the bar. So only trust the button when it is centred in the
     * viewport, and otherwise measure the column it lives in.
     *
     * @param {DOMRect} rect The real button's box.
     * @returns {number|null} Pixels, or null to leave the stylesheet's default.
     */
    gutter(rect) {
      const width = window.innerWidth;
      // A template whose own mobile layout overflows has no honest gutter to
      // find, and its buttons measure wider than the viewport. Floor the result
      // so the bar never runs edge to edge on one of those.
      const sane = (value) =>
        (value >= 0 && value <= width / 3 ? Math.max(12, value) : null);

      if (rect.width > 0 && Math.abs(width - rect.right - rect.left) <= 2) {
        const own = sane(rect.left);
        if (own != null) return own;
      }

      let node = this.mainButton.parentElement;
      while (node && node !== document.body) {
        const box = node.getBoundingClientRect();
        if (box.width >= width * 0.85) {
          const pad = parseFloat(window.getComputedStyle(node).paddingLeft) || 0;
          return sane(box.left + pad);
        }
        node = node.parentElement;
      }
      return null;
    }

    /** Give the page back exactly the height the bar takes off the bottom. */
    reserve() {
      const height = window.innerWidth < HIDE_ABOVE ? this.refs.bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--cp-sb-sticky-h', `${height}px`);
    }

    /* ------------------------------------------------------------- reading */

    variantId() {
      const el = this.form.querySelector('[name="id"]');
      return el ? String(el.value || '') : '';
    }

    variant() {
      return this.variants[this.variantId()] || null;
    }

    /** Every line item property control this template's form actually has. */
    properties() {
      return Array.from(this.form.elements).filter((el) =>
        /^properties\[/.test(el.name || '')
      );
    }

    /**
     * Required personalisation still waiting on the shopper, grouped by
     * property so a radio set counts once.
     *
     * @returns {Array<{name: string, control: Element}>}
     */
    missing() {
      const groups = new Map();
      this.properties().forEach((el) => {
        if (!isLive(el) || !isRequired(el)) return;
        const entry = groups.get(el.name) || { name: el.name, control: el, filled: false };
        if (isFilled(el)) entry.filled = true;
        groups.set(el.name, entry);
      });
      return Array.from(groups.values()).filter((entry) => !entry.filled);
    }

    /**
     * "Blue stars · M · YOUR DOG · 0487 558 825" — the chosen variant's options
     * followed by whatever the shopper has typed, in the order the form asks
     * for it.
     */
    selectionLine() {
      const variant = this.variant();
      const parts = [];

      // A template that wants to describe its own selection can say so, and it
      // wins over the bundle's own name — the harness uses this to report how
      // many spare patches are still unchosen.
      const said = document.querySelector('[data-cp-sb-summary]');
      const summary = said ? String(said.textContent || '').trim() : '';

      const bundle = summary || this.bundle();
      if (bundle) {
        parts.push(bundle);
      } else {
        const qty = this.quantity();
        if (qty > 1) parts.push(`× ${qty}`);
      }

      if (variant && variant.options) parts.push(...variant.options);
      this.properties().forEach((el) => {
        if (!isLive(el) || !isTypedValue(el)) return;
        const value = String(el.value == null ? '' : el.value).trim();
        if (value) parts.push(value);
      });
      return parts.filter(Boolean).join(' · ');
    }

    /** How many of the item the form is actually going to post. */
    quantity() {
      const el = this.form.querySelector('[name="quantity"]');
      const n = el ? parseInt(el.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    /**
     * The price the template's own button is showing. Every Crumpaw hero writes
     * the full charge into its label — bundle quantity and any add-on included —
     * so lifting that number is the only way the two can never disagree. A
     * bundle's add-on is a separate product that never touches this form, so
     * anything recomputed from the variant here would be wrong by its price.
     *
     * @returns {string|null} Null when the button prints no price at all.
     */
    mainPrice() {
      if (this.moneyRe === undefined) this.moneyRe = moneyPattern(this.config.fallbackPrice);
      if (!this.moneyRe) return null;
      const found = String(this.mainButton.textContent || '').match(this.moneyRe);
      return found ? found[0].trim() : null;
    }

    price() {
      const shown = this.mainPrice();
      if (shown) return shown;

      // Stock Horizon prints no price on the button, but it does have a
      // quantity box, so a bare variant price would understate the total.
      const variant = this.variant();
      if (variant && variant.cents != null && this.config.moneyFormat) {
        return formatMoney(variant.cents * this.quantity(), this.config.moneyFormat);
      }
      return (variant && variant.price) || this.config.fallbackPrice || '';
    }

    /**
     * The chosen bundle, when it is not the plain single item. The radio's own
     * value is a block id, so the readable name comes from its label — first
     * line only, which is the title above the subtitle and the price.
     *
     * @returns {string|null}
     */
    bundle() {
      if (!this.config.bundleSelector) return null;
      let inputs;
      try {
        inputs = Array.from(document.querySelectorAll(this.config.bundleSelector));
      } catch (error) {
        return null;
      }
      if (inputs.length < 2) return null;

      const index = inputs.findIndex((el) => el.checked);
      // Option one is the single item, which the rest of the line already
      // describes. Only a deliberate upgrade is worth the space.
      if (index < 1) return null;

      const input = inputs[index];
      const label = input.id ? document.querySelector(`label[for="${input.id}"]`) : null;
      const text = label ? label.innerText : input.value;
      const first = String(text || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)[0];
      return first || null;
    }

    /* ------------------------------------------------------------ painting */

    render() {
      if (!this.refs.bar.isConnected) return;

      const waiting = this.missing();
      this.firstMissing = waiting.length ? waiting[0].control : null;
      const variant = this.variant();

      // Order matters. Several templates disable their own button until the
      // personalisation is valid, so `disabled` on its own says nothing about
      // stock — only the variant does. A disabled button on a page that has
      // personalisation means "there is something left to do up there"; on a
      // page with none, there is nothing to send the shopper back for.
      const personalised = this.properties().some(isLive);
      if (this.inCart) {
        this.state = 'checkout';
      } else if (variant && variant.available === false) {
        this.state = 'unavailable';
      } else if (waiting.length || (this.mainButton.disabled && personalised)) {
        this.state = 'name';
      } else if (this.mainButton.disabled) {
        this.state = 'unavailable';
      } else {
        this.state = 'add';
      }

      let label;
      if (this.state === 'unavailable') {
        label = this.config.labelUnavailable || '';
      } else if (this.state === 'checkout') {
        label = this.config.labelCheckout;
      } else if (this.state === 'name') {
        label = this.config.labelName;
      } else {
        label = interpolate(this.config.labelAdd, { price: this.price() });
      }

      setText(this.refs.label, label);
      setText(this.refs.line, this.selectionLine());
      this.refs.cta.disabled = this.state === 'unavailable';
      this.refs.bar.dataset.cpSbState = this.state;
      this.reserve();
    }

    /* ------------------------------------------------------------- actions */

    onClick(event) {
      event.preventDefault();

      if (this.state === 'unavailable') return;

      if (this.state === 'checkout') {
        window.location.href = this.config.checkoutUrl || '/checkout';
        return;
      }

      if (this.state === 'add') {
        // Click the real button rather than posting a form of our own, so the
        // line item properties, the AJAX cart and any per-template click
        // handler all behave exactly as they do without this bar.
        this.mainButton.click();
        return;
      }

      // Nothing obviously missing, but the template is still holding its own
      // button back — take the shopper to it rather than doing nothing.
      const target = this.firstMissing;
      if (!target) {
        this.mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      // Photo uploads and swatch inputs are visually hidden, so scroll to the
      // nearest thing that actually occupies space.
      let anchor = target;
      while (anchor && anchor.offsetHeight === 0 && anchor.parentElement) {
        anchor = anchor.parentElement;
      }
      const step = this.config.stepSelector
        ? (target.closest(this.config.stepSelector) || anchor)
        : anchor;
      step.scrollIntoView({ behavior: 'smooth', block: 'center' });

      if (target.offsetHeight > 0) {
        window.setTimeout(() => {
          try {
            target.focus({ preventScroll: true });
          } catch (error) {
            target.focus();
          }
        }, 420);
      }
    }

    /**
     * The cart event fires for removals as well as adds, so ask the cart what it
     * holds rather than assuming the last thing that happened was an add.
     */
    refreshCart() {
      const id = String(this.config.productId || '');
      window
        .fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          this.inCart = (cart.items || []).some((item) => String(item.product_id) === id);
          this.render();
        })
        .catch(() => {});
    }
  }

  if (!window.customElements.get('cp-sb-sticky-atc')) {
    window.customElements.define('cp-sb-sticky-atc', CpSbStickyAtc);
  }
})();
